import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Box,
  Button,
  InlineStack,
  Badge,
  Link,
  ButtonGroup,
  Modal,
  TextField,
  Select,
  FormLayout,
  Text,
  SkeletonBodyText,
  SkeletonDisplayText,
  SkeletonThumbnail,
  DataTable,
  Tabs,
  Banner
} from '@shopify/polaris';
import { getRequest, postRequest, putRequest, getCurrentDateTimeLocal } from '../../components/functions';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import InteractionForm from '../../../pages/interactions/components/InteractionForm';
import { interactionTypeGroups } from '../../../pages/interactions/components/InteractionForm';
import InteractionDocuments from '../../../pages/interaction/components/InteractionDocuments';
import { useLookupStore } from '../../../context/useStore.js';
import { CardHeading } from '../cards/CardHeading.jsx';

// Add a style tag to the document head to disable pointer events on the modal backdrop
const addModalBackdropStyle = () => {
  const styleId = 'modal-backdrop-style';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .Polaris-Modal-Backdrop {
        pointer-events: none !important;
      }
      .Polaris-Modal-Dialog {
        pointer-events: auto !important;
      }
    `;
    document.head.appendChild(style);
  }
};

export default function InteractionsTimeline({ resourceType, resourceId, resourceName, showContact = false }) {
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnswer, setAiAnswer] = useState('');
  const [aiError, setAiError] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailContent, setEmailContent] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [editingInteraction, setEditingInteraction] = useState(null);
  const [modalTab, setModalTab] = useState(0);
  const [companyData, setCompanyData] = useState(null);
  const [contactData, setContactData] = useState(null);
  const [error, setError] = useState(null);
  const { users } = useLookupStore();

  const handleModalTabChange = useCallback((selectedTabIndex) => {
    setModalTab(selectedTabIndex);
  }, []);

  // Add the style when the component mounts
  useEffect(() => {
    addModalBackdropStyle();
  }, []);

  useEffect(() => {
    if (resourceId && resourceId !== 'new') {
      fetchInteractions();
      fetchResourceData();
    }
  }, [resourceId, resourceType]);

  const fetchResourceData = async () => {
    if (resourceId === 'new' || !resourceId) return;

    try {
      if (resourceType === 'company') {
        const data = await getRequest(`/api/companies/${resourceId}`);
        if (data) {
          setCompanyData(data);
        }
      } else if (resourceType === 'contact') {
        const data = await getRequest(`/api/contacts/${resourceId}`);
        if (data && data.length > 0) {
          setContactData(data[0]);
        }
      } else if (resourceType === 'project') {
        const data = await getRequest(`/api/projects/${resourceId}`);
        if (data && data.length > 0) {
          setCompanyData(data[0]);
          // If project has a company (firnr), we can use it for company_id
        }
      }
    } catch (error) {
      console.error(`Error fetching resource data:`, error);
      setError('Failed to load resource information');
    }
  };

  const fetchInteractions = async () => {
    if (resourceId === 'new' || !resourceId) return;

    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams();

      if (resourceType === 'company') {
        queryParams.set('company_id', resourceId);
      } else if (resourceType === 'contact') {
        queryParams.set('company_contact_id', resourceId);
      } else if (resourceType === 'project') {
        queryParams.set('project_id', resourceId);
      }

      queryParams.set('limit', '50');

      const data = await getRequest(`/api/interactions?${queryParams.toString()}`);
      setInteractions(Array.isArray(data.interactions) ? data.interactions : []);
    } catch (error) {
      console.error(`Error fetching interactions for ${resourceType}:`, error);
      setInteractions([]);
      setError('Failed to load interactions');
    } finally {
      setLoading(false);
    }
  };

  const getInteractionBadgeColor = (type) => {
    if (!type) return 'default';

    for (const group of Object.values(interactionTypeGroups)) {
      if (group.types.includes(type)) {
        return group.color;
      }
    }
    return 'default';
  };

  const getInitialInteraction = () => {
    const interaction = {
      type: '',
      description: '',
      event_date: getCurrentDateTimeLocal(),
      start_time: '',
      end_time: '',
      company_id: '',
      company_contact_id: '',
      project_id: '',
      title: ''
    };

    if (resourceType === 'company') {
      interaction.company_id = resourceId;
    } else if (resourceType === 'contact') {
      interaction.company_contact_id = resourceId;
      if (contactData?.KLANR) {
        interaction.company_id = contactData.KLANR;
      }
    } else if (resourceType === 'project') {
      interaction.project_id = resourceId;
      // Optionally set company_id if available from project data
      if (companyData?.firnr) {
        interaction.company_id = companyData.firnr;
      }
    }

    return interaction;
  };

  const handleInteractionSubmit = async () => {
    await fetchInteractions();
    setIsViewModalOpen(false);
    setIsNewModalOpen(false);
    setEditingInteraction(null);
  };

  const handleAskAI = async () => {
    if (interactions.length === 0) {
      setAiError('No interactions available to analyze');
      return;
    }

    if (!aiQuestion.trim()) {
      setAiError('Please enter a question');
      return;
    }

    setAiLoading(true);
    setAiError(null);
    setAiAnswer('');

    try {
      const response = await fetch('/api/openai/ask-about-interactions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          interactions,
          question: aiQuestion.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get AI response');
      }

      const data = await response.json();
      setAiAnswer(data.answer);
    } catch (error) {
      console.error('Error asking AI:', error);
      setAiError(error.message || 'Failed to get AI response');
    } finally {
      setAiLoading(false);
    }
  };

  const handleOpenAIModal = () => {
    setIsAIModalOpen(true);
    setAiQuestion('');
    setAiAnswer('');
    setAiError(null);
  };

  const handleWriteFollowupEmail = async () => {
    if (interactions.length === 0) {
      setEmailError('No interactions available to generate email');
      return;
    }

    setEmailLoading(true);
    setEmailError(null);
    setEmailContent('');
    setIsEmailModalOpen(true);

    try {
      // Determine language - prefer contact language, fall back to company language
      let taalID = null;
      if (contactData?.TaalID) {
        taalID = contactData.TaalID;
      } else if (companyData?.TaalID) {
        taalID = companyData.TaalID;
      }

      const response = await fetch('/api/openai/write-followup-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          interactions,
          taalID,
          companyData,
          contactData,
          resourceType,
          resourceId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate follow-up email');
      }

      const data = await response.json();
      setEmailContent(data.email);
    } catch (error) {
      console.error('Error generating follow-up email:', error);
      setEmailError(error.message || 'Failed to generate follow-up email');
    } finally {
      setEmailLoading(false);
    }
  };

  // Very small markdown -> HTML renderer for bold and lists; safe because we escape HTML first
  const markdownToHtml = (markdown) => {
    if (!markdown) return '';
    // Escape HTML
    const escapeHtml = (str) => str.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

    const lines = escapeHtml(markdown).split(/\r?\n/);
    let html = '';
    let inUl = false;
    let inOl = false;

    const flushLists = () => {
      if (inUl) {
        html += '</ul>';
        inUl = false;
      }
      if (inOl) {
        html += '</ol>';
        inOl = false;
      }
    };

    for (const rawLine of lines) {
      let line = rawLine;
      // Bold **text**
      line = line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

      // Ordered list: "1. text"
      const olMatch = line.match(/^\s*\d+\.\s+(.*)$/);
      if (olMatch) {
        if (!inOl) {
          flushLists();
          html += '<ol>';
          inOl = true;
        }
        html += `<li>${olMatch[1]}</li>`;
        continue;
      }

      // Unordered list: "- text" or "* text"
      const ulMatch = line.match(/^\s*[-*]\s+(.*)$/);
      if (ulMatch) {
        if (!inUl) {
          flushLists();
          html += '<ul>';
          inUl = true;
        }
        html += `<li>${ulMatch[1]}</li>`;
        continue;
      }

      // Empty line => paragraph break
      if (line.trim() === '') {
        flushLists();
        html += '<br />';
        continue;
      }

      // Normal paragraph line
      flushLists();
      html += `<p>${line}</p>`;
    }

    flushLists();
    return html;
  };

  if (resourceId === 'new' || !resourceId) {
    return null;
  }

  if (loading && interactions.length === 0) {
    return (
      <Card>
        <Box>
          <Box paddingBlockEnd="200">
            <InlineStack align="space-between">
              <CardHeading title="Interactions" />
              <Button primary onClick={() => setIsNewModalOpen(true)} loading={loading}>
                New Interaction
              </Button>
            </InlineStack>
          </Box>
          <div style={{ padding: '16px' }}>Loading interactions...</div>
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Box>
          <Box paddingBlockEnd="200">
            <InlineStack align="space-between">
              <CardHeading title="Interactions" />
              <Button primary onClick={() => setIsNewModalOpen(true)}>
                New Interaction
              </Button>
            </InlineStack>
          </Box>
          <div style={{ padding: '16px', color: 'rgb(220, 53, 69)' }}>
            {error}. <Link onClick={fetchInteractions}>Try again</Link>
          </div>
        </Box>
      </Card>
    );
  }

  const rows = interactions.length
    ? interactions.map((interaction) => {
        // Count attached documents
        let docCount = 0;
        if (interaction.S3ObjectKeys) {
          try {
            const keys = JSON.parse(interaction.S3ObjectKeys);
            docCount = Array.isArray(keys) ? keys.length : 0;
          } catch (e) {
            // Invalid JSON, ignore
          }
        }

        // Build project cell (truncated to ~15 chars)
        let projectCell = '-';
        if (interaction.project_id && interaction.project_number) {
          const projectDisplay = String(interaction.project_number);
          const truncatedProject = projectDisplay.length > 15 ? `${projectDisplay.slice(0, 15)}...` : projectDisplay;
          projectCell = (
            <Link url={`/projects/${interaction.project_id}`} removeUnderline monochrome>
              <Text as="span" variant="bodyMd">
                {truncatedProject}
              </Text>
            </Link>
          );
        }

        const baseRow = [
          String(interaction.event_date || '')
            .replace('T', ' ')
            .slice(0, 16),
          <Badge tone={getInteractionBadgeColor(interaction.type)}>{interaction.type || 'Unknown'}</Badge>,
          <InlineStack gap="200" blockAlign="center">
            <Link url={`/interactions/${interaction.id}`} monochrome>
              {interaction.title?.length > 40 ? `${interaction.title.slice(0, 40)}...` : interaction.title || 'No title'}
            </Link>
            {docCount > 0 && <Badge tone="success">📎 {docCount}</Badge>}
          </InlineStack>,
          projectCell,
          users.find((user) => user.GebruikerID === interaction.user_id)?.Afkorting || 'N/A',
          <ButtonGroup>
            <Button
              size="slim"
              onClick={() => {
                setEditingInteraction(interaction);
                setModalTab(0);
                setIsViewModalOpen(true);
              }}
            >
              Edit
            </Button>
            <Button
              size="slim"
              onClick={() => {
                window.location.href = `/interactions/${interaction.id}`;
              }}
            >
              View
            </Button>
          </ButtonGroup>
        ];

        if (showContact && resourceType === 'company') {
          baseRow.splice(2, 0, interaction.contact_name ? <Link url={`/contacts/${interaction.contact_id}`}>{interaction.contact_name}</Link> : '-');
        }

        return baseRow;
      })
    : [];

  const headings = ['Date', 'Type'];
  if (showContact && resourceType === 'company') {
    headings.push('Contact');
  }
  headings.push('Title', 'Project', 'User', 'Actions');

  return (
    <Card>
      <Box>
        <Box paddingBlockEnd="200">
          <InlineStack align="space-between">
            <CardHeading title="Interactions" />
            <InlineStack gap="200">
              {interactions.length > 0 && (
                <>
                  <Button onClick={handleOpenAIModal} disabled={loading}>
                    ASK with AI
                  </Button>
                  <Button onClick={handleWriteFollowupEmail} disabled={loading}>
                    Write follow up email
                  </Button>
                </>
              )}
              <Button primary onClick={() => setIsNewModalOpen(true)}>
                New Interaction
              </Button>
            </InlineStack>
          </InlineStack>
        </Box>

        {interactions.length > 0 ? (
          <DataTable columnContentTypes={Array(headings.length).fill('text')} headings={headings} rows={rows} verticalAlign="middle" increasedTableDensity />
        ) : (
          <div style={{ padding: '16px' }}>
            <Text>No interactions found. Click "New Interaction" to create one.</Text>
          </div>
        )}

        <Box paddingBlockStart="400">
          <InlineStack align="end">
            <Link url={`/interactions?${resourceType === 'company' ? 'company_id=' : 'company_contact_id='}${resourceId}`}>View all interactions</Link>
          </InlineStack>
        </Box>
      </Box>

      {/* AI Question Modal */}
      <Modal
        open={isAIModalOpen}
        onClose={() => {
          setIsAIModalOpen(false);
          setAiQuestion('');
          setAiAnswer('');
          setAiError(null);
        }}
        title="ASK with AI"
        size="large"
        primaryAction={{
          content: aiAnswer ? 'Close' : 'Ask',
          onAction: aiAnswer
            ? () => {
                setIsAIModalOpen(false);
                setAiQuestion('');
                setAiAnswer('');
                setAiError(null);
              }
            : handleAskAI,
          loading: aiLoading,
          disabled: aiLoading || !aiQuestion.trim()
        }}
        secondaryActions={
          aiAnswer
            ? [
                {
                  content: 'Ask Another Question',
                  onAction: () => {
                    setAiQuestion('');
                    setAiAnswer('');
                    setAiError(null);
                  }
                }
              ]
            : []
        }
      >
        <Modal.Section>
          <FormLayout>
            <Box paddingBlockEnd="400">
              <Text as="p" tone="subdued">
                The AI analyzes all interactions and their attached documents. You can ask questions about the content of PDFs, images, and other files that have been uploaded with these interactions.
                The AI uses these documents as context when answering your questions.
              </Text>
            </Box>
            <TextField
              label="Ask a question about these interactions"
              value={aiQuestion}
              onChange={setAiQuestion}
              placeholder="e.g., What are the main concerns mentioned? What follow-ups are needed?"
              multiline={3}
              disabled={aiLoading || !!aiAnswer}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !aiLoading && aiQuestion.trim()) {
                  e.preventDefault();
                  handleAskAI();
                }
              }}
            />
            {aiError && (
              <div style={{ padding: '16px', color: 'rgb(220, 53, 69)' }}>
                <Text as="p" tone="critical">
                  {aiError}
                </Text>
              </div>
            )}
            {aiLoading && (
              <div style={{ padding: '16px' }}>
                <Text>Thinking...</Text>
              </div>
            )}
            {aiAnswer && (
              <div style={{ padding: '16px', border: '1px solid #e1e3e5', borderRadius: '6px', backgroundColor: '#f6f6f7' }}>
                <div className="ai-answer-markdown" dangerouslySetInnerHTML={{ __html: markdownToHtml(aiAnswer) }} />
              </div>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Follow-up Email Modal */}
      <Modal
        open={isEmailModalOpen}
        onClose={() => {
          setIsEmailModalOpen(false);
          setEmailContent('');
          setEmailError(null);
        }}
        title="Follow-up Email"
        size="large"
        primaryAction={{
          content: 'Close',
          onAction: () => {
            setIsEmailModalOpen(false);
            setEmailContent('');
            setEmailError(null);
          },
          disabled: emailLoading
        }}
      >
        <Modal.Section>
          <FormLayout>
            {emailError && (
              <div style={{ padding: '16px', color: 'rgb(220, 53, 69)' }}>
                <Text as="p" tone="critical">
                  {emailError}
                </Text>
              </div>
            )}
            {emailLoading && (
              <div style={{ padding: '16px' }}>
                <Text>Generating follow-up email...</Text>
              </div>
            )}
            {emailContent && !emailLoading && (
              <div style={{ padding: '16px', border: '1px solid #e1e3e5', borderRadius: '6px', backgroundColor: '#f6f6f7' }}>
                <div
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    lineHeight: '1.6'
                  }}
                >
                  {emailContent}
                </div>
              </div>
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* View/Edit Interaction Modal */}
      <Modal
        open={isViewModalOpen}
        onClose={() => {
          // This will only be triggered by the close button, not by clicking outside
          setIsViewModalOpen(false);
          setEditingInteraction(null);
          setModalTab(0);
        }}
        title="View Interaction"
        size="large"
      >
        <Modal.Section>
          {editingInteraction && (editingInteraction.id || editingInteraction.ID) ? (
            <>
              {/* Show banner if documents are attached */}
              {editingInteraction.S3ObjectKeys &&
                (() => {
                  try {
                    const keys = JSON.parse(editingInteraction.S3ObjectKeys);
                    const docCount = Array.isArray(keys) ? keys.length : 0;
                    if (docCount > 0) {
                      return (
                        <Box paddingBlockEnd="400">
                          <Banner tone="info">
                            <InlineStack gap="200" align="space-between" blockAlign="center">
                              <Text as="span">
                                {docCount} {docCount === 1 ? 'document' : 'documents'} attached to this interaction
                              </Text>
                              {modalTab === 0 && (
                                <Button size="slim" onClick={() => setModalTab(1)}>
                                  View documents
                                </Button>
                              )}
                            </InlineStack>
                          </Banner>
                        </Box>
                      );
                    }
                  } catch (e) {
                    // Invalid JSON, ignore
                  }
                  return null;
                })()}

              <Tabs
                tabs={[
                  {
                    id: 'details',
                    content: 'Details',
                    panelID: 'details-panel'
                  },
                  {
                    id: 'documents',
                    content: 'Documents',
                    panelID: 'documents-panel'
                  }
                ]}
                selected={modalTab}
                onSelect={handleModalTabChange}
              >
                {modalTab === 0 && (
                  <div id="details-panel" role="tabpanel">
                    <InteractionForm interaction={editingInteraction} onSubmit={handleInteractionSubmit} />
                  </div>
                )}
                {modalTab === 1 && (editingInteraction.id || editingInteraction.ID) && (
                  <div id="documents-panel" role="tabpanel">
                    <InteractionDocuments interactionId={editingInteraction.id || editingInteraction.ID} />
                  </div>
                )}
              </Tabs>
            </>
          ) : (
            <InteractionForm interaction={editingInteraction} onSubmit={handleInteractionSubmit} />
          )}
        </Modal.Section>
        <Modal.Section>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setIsViewModalOpen(false);
                setEditingInteraction(null);
                setModalTab(0);
              }}
            >
              Close
            </Button>
          </div>
        </Modal.Section>
      </Modal>

      {/* New Interaction Modal */}
      <Modal open={isNewModalOpen} onClose={() => setIsNewModalOpen(false)} title="New Interaction" size="large">
        <Modal.Section>
          <InteractionForm interaction={getInitialInteraction()} isNew={true} onSubmit={handleInteractionSubmit} />
        </Modal.Section>
        <Modal.Section>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setIsNewModalOpen(false)}>Close</Button>
          </div>
        </Modal.Section>
      </Modal>
    </Card>
  );
}
